import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AircraftDetail, PaginatedTechlog } from 'types/techlog'
import { Button, Stack, Container, Inline, Typography } from '@airbus/components-react/components'
import useQuery from 'utils/hooks/useQuery'
import urlApis from 'utils/api/urlApis'
import LangContext from 'context/LangContext'
import { useSearchParams } from 'react-router-dom'
import URL_APIS from 'utils/api/urlApis'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@airbus/components-react/components'
import ComponentsUNames from 'constants/componentsUNames'
import Config from 'constants/config'
import LocalStorage from 'utils/myLocalStorage'
import PageHeader from 'components/pageHeader'
import debounce from 'lodash.debounce'
import { Pagination as TablePagination } from '@airbus/components-react/components'
import { CustomSelect } from 'components/FormFields/FormSelect'

const PAGINATION_DEFAULTS = {
  nbItems: 0,
  pageSize: LocalStorage.getOrDefault(
    [
      LocalStorage.KEY_PREFS,
      ComponentsUNames.CRM_STORE_NAME,
      ComponentsUNames.CRM_TABLE_UNAME + '|' + ComponentsUNames.PROP_PAGE_SIZE,
    ],
    LocalStorage.VALUE_TYPE.INTEGER,
    Config.PAGINATION_DEFAULT_PAGESIZE,
  ),
  pageNo: 1,
  nbPages: 0,
  choiceNbRows: [10, 20, 30, 40, 50],
}

const TechlogSearchPage = () => {
  const baseUrl = process.env.PUBLIC_URL
  const { t } = useTranslation()

  const [searchParams, setSearchParams] = useSearchParams()
  const selectedAvimmatr = searchParams.get('search')
  const tlToFind = searchParams.get('id')

  const navItems = [
    {
      title: t('7001825'),
      url: baseUrl + '/techlogs',
    },
  ]
  if (selectedAvimmatr)
    navItems.push({
      title: selectedAvimmatr,
      url: window.location.href,
    })

  const [selectedAvnumap, setSelectedAvnumap] = useState<string>()

  const [aircrafts, setAircrafts] = useState<AircraftDetail[]>([])
  const { fetchData: fetchAircrafts } = useQuery<AircraftDetail[]>(URL_APIS.URL_CRM_AC_GET)

  const [techlogs, setTechlogs] = useState<PaginatedTechlog | null>()
  const { fetchData: fetchTechlogs } = useQuery<PaginatedTechlog>(urlApis.URL_CRM_GET)

  const navigate = useNavigate()
  const { lang } = useContext(LangContext)

  /***************************/
  /**** PAGINATION START *****/
  /***************************/
  const [rowsPerPageOptions, setRowsPerPageOptions] = useState(PAGINATION_DEFAULTS.choiceNbRows)
  // Setters which are used during user's pagination
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_DEFAULTS.pageSize)
  const [page, setPage] = useState(PAGINATION_DEFAULTS.pageNo)
  // Setters which reflect endpoints' returned lists
  const [nbItems, setNbItems] = useState(PAGINATION_DEFAULTS.nbItems)
  const [totalPages, setTotalPages] = useState(PAGINATION_DEFAULTS.nbPages)

  // Pagination
  const pageSizeChangeHandler = (event) => {
    const selectedPageSize = event.target.value

    if (selectedPageSize !== rowsPerPage) {
      // Sync with local storage
      const propToUpdate = [
        LocalStorage.KEY_PREFS,
        ComponentsUNames.CRM_STORE_NAME,
        ComponentsUNames.CRM_TABLE_UNAME + '|' + ComponentsUNames.PROP_PAGE_SIZE,
      ]
      LocalStorage.put(propToUpdate, selectedPageSize)
      // Sync the component's context pagination
      setRowsPerPage(parseInt(selectedPageSize))
      // Go back on first page
      setPage(1)
      //onSyncContextPagination(componentUName, contextPagination)
    }
  }

  const pageNoChangeHandler = (event, newPage: number) => {
    if (selectedAvimmatr && newPage !== page) {
      setPage(newPage)
      setSearchParams({ search: selectedAvimmatr })
    }
  }

  const updatePagination = (techlogsData) => {
    if (techlogsData) {
      setPage(techlogsData?.pageable?.pageNumber + 1)
      setTotalPages(techlogsData?.totalPages)
      setNbItems(techlogsData?.totalElements)
    }
  }

  useEffect(() => {
    if (selectedAvnumap && !isNaN(page) && rowsPerPage) {
      ;(async () => {
        const techlogResponse = await fetchTechlogs({
          lang,
          search: selectedAvnumap,
          id: tlToFind,
          size: rowsPerPage,
          page: page - 1,
        })
        setTechlogs(techlogResponse?.data)
        updatePagination(techlogResponse?.data)
      })()
    }
  }, [page, rowsPerPage])

  /***************************/
  /**** PAGINATION END *******/
  /***************************/

  const canCreate = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.CRM_CREATE_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const loadAircrafts = async (avimmatr: string): Promise<AircraftDetail[] | null> => {
    let aircrafts: AircraftDetail[] = []
    if (avimmatr) {
      //onLoadInProgress(item.uniqueName, true)
      const response = await fetchAircrafts({ avimmatr: avimmatr.toUpperCase(), lang })
      //onLoadInProgress(item.uniqueName, false)
      aircrafts = response?.data || []
    }
    setAircrafts(aircrafts)
    return aircrafts
  }

  const handleInputChange = debounce(({ inputValue: avimmatr }) => {
    if (avimmatr) {
      setSearchParams({ search: avimmatr })
      setTechlogs(null)
    }
  }, 600)

  const handleSelectionTL = (avimmatr: string, rmdate: number[], rmdoc: string) => {
    const yyyy = rmdate[0]
    const mm = ('0' + rmdate[1]).slice(-2)
    const jj = ('0' + rmdate[2]).slice(-2)
    navigate(`/amasis-web/techlogs/${avimmatr}/${yyyy}${mm}${jj}/${rmdoc}`)
  }

  const handleCrmCreateRequest = () => {
    navigate(`/amasis-web/techlogs/${selectedAvimmatr}/new`)
  }

  useEffect(() => {
    if (selectedAvimmatr) {
      // Re-fetch pageSize from LS in case we arrive on this page using the Breadcrumb
      const pageSize = LocalStorage.getOrDefault(
        [
          LocalStorage.KEY_PREFS,
          ComponentsUNames.CRM_STORE_NAME,
          ComponentsUNames.CRM_TABLE_UNAME + '|' + ComponentsUNames.PROP_PAGE_SIZE,
        ],
        LocalStorage.VALUE_TYPE.INTEGER,
        Config.PAGINATION_DEFAULT_PAGESIZE,
      )
      setRowsPerPage(pageSize)
      ;(async () => {
        const acs = await loadAircrafts(selectedAvimmatr)
        const selectedAircraft = acs?.find((a) => a.avimmatr === selectedAvimmatr)
        const aircraftAvnumap = selectedAircraft?.avnumap
        if (aircraftAvnumap) {
          setSelectedAvnumap(aircraftAvnumap)

          if (tlToFind && tlToFind != '') {
            // Get page-number for tlToFind and request this page without tlToFind
            const techlogResponse = await fetchTechlogs({
              lang,
              search: aircraftAvnumap,
              id: tlToFind,
              size: pageSize,
              page: page - 1,
            })
            setPage(techlogResponse?.data?.pageable?.pageNumber + 1)
            setSearchParams({ search: selectedAvimmatr })
          } else {
            const techlogResponse = await fetchTechlogs({
              lang,
              search: aircraftAvnumap,
              id: tlToFind,
              size: pageSize,
              page: page - 1,
            })
            setTechlogs(techlogResponse?.data)
            updatePagination(techlogResponse?.data)
          }
        }
      })()
    }

    return () => {
      setTechlogs(null)
      setSelectedAvnumap('')
    }
  }, [selectedAvimmatr, tlToFind, lang])

  return (
    <>
      <PageHeader pageTitle={t('7001825')} navItems={navItems} />
      <Container style={{ flex: 1 }} until='xl'>
        <Stack spacing='5-x'>
          <Inline align='space-between'>
            <CustomSelect
              options={aircrafts.map((a) => a.avimmatr)}
              label={t('9500031')}
              placeholder={t('placeholder_techlogs_avimmatr')}
              filterMatch='STARTS_WITH'
              onInputValueChange={handleInputChange}
              value={selectedAvimmatr}
              rules={{ required: true }}
              renderNoMatch={() => '-'}
              style={{ width: '300px' }}
              capitalize
              disableOnInputChange
            />
            <Button
              variant='primary'
              disabled={!selectedAvnumap || canCreate !== true}
              onClick={handleCrmCreateRequest}
            >
              {t('9500160')}
            </Button>
          </Inline>
          <Stack style={{ marginTop: '2em' }}>
            {techlogs &&
            techlogs?.content?.length > 0 &&
            JSON.stringify(techlogs) !== '{}' &&
            JSON.stringify(techlogs) !== '[]' ? (
              <Table>
                <colgroup>
                  {/* N° CRM */}
                  <col style={{ width: '14%' }} />
                  {/*	Date */}
                  <col style={{ width: '14%' }} />
                  {/* N° de vol	*/}
                  <col style={{ width: '12%' }} />
                  {/* Départ */}
                  <col style={{ width: '10%' }} />
                  {/* Arrivée */}
                  <col style={{ width: '10%' }} />
                  {/*	Durée de vol (AB) */}
                  <col style={{ width: '12%' }} />
                  {/* FH */}
                  <col style={{ width: '12%' }} />
                  {/* Cycle */}
                  <col style={{ width: '12%' }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('0MOE065')}</TableCell>
                    <TableCell>{t('9500034')}</TableCell>
                    <TableCell>{t('7002927')}</TableCell>
                    <TableCell>{t('0MRM025')}</TableCell>
                    <TableCell>{t('1MRM013')}</TableCell>
                    <TableCell align='right'>{t('9500158')}</TableCell>
                    <TableCell align='right'>{t('0MBO012')}</TableCell>
                    <TableCell align='right'>{t('0MRM024')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {techlogs?.content ? (
                    techlogs.content.map((d, i) => (
                      <TableRow
                        id={d.rmdoc}
                        key={'line_crms_' + i}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSelectionTL(d.avimmatr, d.rmdate, d.rmdoc)}
                        sx={{ fontWeight: d.rmdoc === d.lastCrm && 'font.fontWeightBold' }}
                      >
                        <TableCell>{d.rmdoc}</TableCell>
                        <TableCell>
                          {('0' + d.rmdate[2]).slice(-2) + '/' + ('0' + d.rmdate[1]).slice(-2) + '/' + d.rmdate[0]}
                        </TableCell>
                        <TableCell>{d.vovolnum ? d.vovolnum : '-'}</TableCell>
                        <TableCell>{d.voaerodep}</TableCell>
                        <TableCell>{d.voaeroarr}</TableCell>
                        <TableCell align='right'>{d.voabtps ? d.voabtps : '-'}</TableCell>
                        <TableCell align='right'>{d.fh}</TableCell>
                        <TableCell align='right'>{d.cycle}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow />
                  )}
                </TableBody>

                <TableRow>
                  <TableCell colSpan='8'>
                    <TablePagination
                      count={nbItems}
                      page={page}
                      onPageChange={pageNoChangeHandler}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={pageSizeChangeHandler}
                      rowsPerPageOptions={rowsPerPageOptions}
                      showRowsPerPage
                    />
                  </TableCell>
                </TableRow>
              </Table>
            ) : (
              <Typography style={{ textAlign: 'center' }}>{t('9500055')}</Typography>
            )}
          </Stack>
        </Stack>
      </Container>
    </>
  )
}

export default TechlogSearchPage
